/**
 * =====================================================================
 * IMPORTANT - READ BEFORE MODIFYING ANY LIST ENDPOINT IN THIS FILE:
 * =====================================================================
 *
 * TNN's Django backend paginates ALL list endpoints by default:
 *
 *   API returns: { "count": N, "results": [...actual data...] }
 *   baseApi unwraps to: { "count": N, "results": [...] }  (NOT an array)
 *
 * Every ``build.query<Type[], ...>`` endpoint MUST have:
 *
 *   transformResponse: (r: { results?: Type[] }) =>
 *     Array.isArray(r?.results) ? r.results : [],
 *
 * Without this, ``.map()`` on the paginated object either crashes
 * ("TypeError: x.map is not a function") or silently shows nothing.
 * This was the #1 bug class in the v1.6 and v4.0.1 QA reports.
 * =====================================================================
 */
/**
 * Billing API endpoints.
 *
 * Admin (TNN):
 *   GET    /api/v1/control/billing/invoices/
 *   POST   /api/v1/control/billing/invoices/
 *   GET    /api/v1/control/billing/invoices/{id}/
 *   PATCH  /api/v1/control/billing/invoices/{id}/
 *   POST   /api/v1/control/billing/invoices/{id}/send/
 *   POST   /api/v1/control/billing/invoices/{id}/void/
 *   POST   /api/v1/control/billing/invoices/{id}/record-payment/
 *   GET    /api/v1/control/billing/profiles/
 *   PATCH  /api/v1/control/billing/profiles/{id}/
 *   GET    /api/v1/control/billing/profiles/cohort-summary/
 *
 * Tenant:
 *   GET    /api/v1/billing/my-invoices/  (own invoices, no drafts)
 */
import { baseApi } from "@/app/store/api/baseApi";

import type { Paginated } from "@/shared/api/types";

import type {
  CohortBillingSummary,
  Invoice,
  InvoiceStatus,
  PaymentRecord,
  SubsidyStatus,
  TenantBillingProfile,
} from "../model/types";

interface CreateInvoiceInput {
  tenant: number;
  amount_due_cents: number;
  period_start: string;
  period_end: string;
  due_date: string;
  line_items?: Array<{ description: string; amount_cents: number; qty: number }>;
  notes?: string;
}

interface RecordPaymentInput {
  amount_cents: number;
  received_at: string;
  method?: string;
  reference?: string;
  notes?: string;
}

interface UpdateBillingProfileInput {
  subsidy_status?: SubsidyStatus;
  monthly_fee_cents?: number;
  grant_source?: string;
  notes?: string;
}

/** RTK Query endpoints for billing (invoices, payment records). */
export const billingApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // ---- Admin: Invoices ----

    listAdminInvoices: build.query<
      Paginated<Invoice>,
      { tenant?: number; status?: InvoiceStatus[] } | void
    >({
      query: (params) => {
        const q: Record<string, string | number | string[]> = {};
        if (params?.tenant != null) q.tenant = params.tenant;
        if (params?.status?.length) q.status = params.status;
        return { url: "/control/billing/invoices/", params: q as Record<string, string | number> };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: "AdminInvoice" as const, id })),
              { type: "AdminInvoice" as const, id: "LIST" },
            ]
          : [{ type: "AdminInvoice" as const, id: "LIST" }],
    }),

    getAdminInvoice: build.query<Invoice, number>({
      query: (id) => `/control/billing/invoices/${id}/`,
      providesTags: (_r, _e, id) => [{ type: "AdminInvoice", id }],
    }),

    createInvoice: build.mutation<Invoice, CreateInvoiceInput>({
      query: (input) => ({
        url: "/control/billing/invoices/",
        method: "POST",
        body: input,
      }),
      invalidatesTags: [
        { type: "AdminInvoice", id: "LIST" },
        { type: "BillingProfile", id: "LIST" },
        { type: "BillingCohortSummary", id: "ONLY" },
      ],
    }),

    sendInvoice: build.mutation<Invoice, number>({
      query: (id) => ({
        url: `/control/billing/invoices/${id}/send/`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "AdminInvoice", id },
        { type: "AdminInvoice", id: "LIST" },
        { type: "Invoice", id: "LIST" }, // tenant view updates too
      ],
    }),

    voidInvoice: build.mutation<Invoice, number>({
      query: (id) => ({
        url: `/control/billing/invoices/${id}/void/`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "AdminInvoice", id },
        { type: "AdminInvoice", id: "LIST" },
        { type: "BillingCohortSummary", id: "ONLY" },
      ],
    }),

    recordPayment: build.mutation<
      { payment: PaymentRecord; invoice: Invoice },
      { id: number; input: RecordPaymentInput }
    >({
      query: ({ id, input }) => ({
        url: `/control/billing/invoices/${id}/record-payment/`,
        method: "POST",
        body: input,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AdminInvoice", id },
        { type: "AdminInvoice", id: "LIST" },
        { type: "BillingProfile", id: "LIST" },
        { type: "BillingCohortSummary", id: "ONLY" },
      ],
    }),

    // ---- Admin: Billing Profiles ----

    listBillingProfiles: build.query<Paginated<TenantBillingProfile>, void>({
      query: () => "/control/billing/profiles/",
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({
                type: "BillingProfile" as const,
                id,
              })),
              { type: "BillingProfile" as const, id: "LIST" },
            ]
          : [{ type: "BillingProfile" as const, id: "LIST" }],
    }),

    updateBillingProfile: build.mutation<
      TenantBillingProfile,
      { id: number; patch: UpdateBillingProfileInput }
    >({
      query: ({ id, patch }) => ({
        url: `/control/billing/profiles/${id}/`,
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "BillingProfile", id },
        { type: "BillingProfile", id: "LIST" },
        { type: "BillingCohortSummary", id: "ONLY" },
      ],
    }),

    getCohortBillingSummary: build.query<CohortBillingSummary, void>({
      query: () => "/control/billing/profiles/cohort-summary/",
      providesTags: [{ type: "BillingCohortSummary", id: "ONLY" }],
    }),

    // ---- Tenant ----

    listMyInvoices: build.query<Paginated<Invoice>, void>({
      query: () => "/billing/my-invoices/",
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: "Invoice" as const, id })),
              { type: "Invoice" as const, id: "LIST" },
            ]
          : [{ type: "Invoice" as const, id: "LIST" }],
    }),
  }),
});

export const {
  useListAdminInvoicesQuery,
  useGetAdminInvoiceQuery,
  useCreateInvoiceMutation,
  useSendInvoiceMutation,
  useVoidInvoiceMutation,
  useRecordPaymentMutation,
  useListBillingProfilesQuery,
  useUpdateBillingProfileMutation,
  useGetCohortBillingSummaryQuery,
  useListMyInvoicesQuery,
} = billingApi;
